import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Loader2, MailCheck } from "lucide-react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const WAIT = 60;

export default function PasswordDimenticata() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState(state?.email || "");
  const [code, setCode] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(0);
  useEffect(() => { if (wait <= 0) return undefined; const t = setTimeout(() => setWait((w) => w - 1), 1000); return () => clearTimeout(t); }, [wait]);

  const send = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() }, { retryable: true });
      toast.success(data.message);
      setStep("code"); setWait(WAIT);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
  };
  const reset = async (e) => {
    e.preventDefault();
    if (pwd !== pwd2) return toast.error("Le due password non coincidono");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email: email.trim(), code, new_password: pwd });
      toast.success("Password aggiornata: ora accedi con quella nuova");
      navigate("/login", { replace: true });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-7 sm:p-8 space-y-6 animate-in-up" data-testid="forgot-page">
        <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="forgot-back-link"><ArrowLeft className="w-4 h-4" /> Torna al login</Link>
        <div className="space-y-2">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">{step === "email" ? <KeyRound className="w-5 h-5" /> : <MailCheck className="w-5 h-5" />}</div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">{step === "email" ? "Password dimenticata?" : "Controlla la tua email"}</h1>
          <p className="text-sm text-muted-foreground" data-testid="forgot-subtitle">
            {step === "email" ? "Scrivi l'email con cui accedi: ti mandiamo un codice di 6 cifre per scegliere una nuova password."
              : <>Se <b className="text-foreground">{email}</b> è registrata, ti abbiamo mandato un codice di 6 cifre. Vale 15 minuti.</>}
          </p>
        </div>
        {step === "email" ? (
          <form onSubmit={send} className="space-y-4">
            <div className="space-y-2"><Label htmlFor="fp-email">Email</Label><Input id="fp-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@azienda.it" data-testid="forgot-email-input" /></div>
            <Button type="submit" className="w-full h-11" disabled={busy || !email.trim()} data-testid="forgot-send-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Inviami il codice"}</Button>
          </form>
        ) : (
          <form onSubmit={reset} className="space-y-4">
            <div className="space-y-2"><Label htmlFor="fp-code">Codice di 6 cifre</Label>
              <Input id="fp-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" className="text-center text-2xl tracking-[0.5em] font-semibold h-14" data-testid="forgot-code-input" />
            </div>
            <div className="space-y-2"><Label htmlFor="fp-pwd">Nuova password</Label><Input id="fp-pwd" type="password" autoComplete="new-password" required minLength={8} value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Almeno 8 caratteri" data-testid="forgot-new-password-input" /></div>
            <div className="space-y-2"><Label htmlFor="fp-pwd2">Ripeti la nuova password</Label><Input id="fp-pwd2" type="password" autoComplete="new-password" required value={pwd2} onChange={(e) => setPwd2(e.target.value)} data-testid="forgot-confirm-password-input" /></div>
            <Button type="submit" className="w-full h-11" disabled={busy || code.length !== 6 || pwd.length < 8} data-testid="forgot-reset-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Imposta la nuova password"}</Button>
            <div className="flex items-center justify-between text-sm">
              <button type="button" onClick={() => { setStep("email"); setCode(""); }} className="text-muted-foreground hover:text-foreground" data-testid="forgot-change-email">Cambia email</button>
              <button type="button" onClick={send} disabled={wait > 0 || busy} className="font-medium text-primary disabled:text-muted-foreground" data-testid="forgot-resend-button">{wait > 0 ? `Invia di nuovo tra ${wait}s` : "Invia di nuovo il codice"}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
